import React, { useState } from 'react';
import {
  Box,
  Button,
  styled,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { InvestorOpportunitySummaryDTO, PsiService } from '@inversor-hub/types';
import ReserveOpportunityModal from '../ReserveOpportunityModal';
import { useRejectOpportunity, useReserveOpportunity } from '@/lib/api/opportunities.ts';
import { StyledCard, StyledCardMedia, PlaceholderIcon } from '@/components/ui/CardStyles';
import { enqueueSnackbar } from 'notistack';
import { StackedSnackbar } from '@/components/ui/StackedSnackbar';
import { InvestmentTypeBadge } from './InvestmentTypeBadge';
import { OpportunityCardContent } from './OpportunityCardContent';
import { OpportunityCTA } from './OpportunityCTA';
import { MatchingIndicator } from '@/components/ui/MatchingIndicator';
import { InterestConfirmationModal } from '../InterestConfirmationModal';

const CTAButton = styled(Button)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 6,
  padding: '10px 16px',
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  transition: 'all 0.2s ease-in-out',
}));

const InterestedButton = styled(Button)(({ theme }) => ({
  width: '100%',
  backgroundColor: alpha(theme.palette.success.main, 0.1),
  color: theme.palette.success.dark,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 6,
  padding: '10px 16px',
  fontSize: '0.875rem',
  border: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
  cursor: 'default',
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
  },
  '&.Mui-disabled': {
    color: theme.palette.success.dark,
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    border: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
  },
}));

interface BaseOpportunityCardProps {
  opportunity: InvestorOpportunitySummaryDTO;
  onAddInterest: (id: string, selectedServices: PsiService[]) => void;
}

export const BaseOpportunityCard = ({ opportunity, onAddInterest }: BaseOpportunityCardProps) => {
  const navigate = useNavigate();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the CTA button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    navigate(`/oportunidades/${opportunity.id}`);
  };

  const handleInterestClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (opportunity.interestStatus !== undefined && opportunity.interestStatus !== 'REMOVED') {
      return;
    }
    setConfirmModalOpen(true);
  };

  const handleConfirmInterest = async (selectedServices: PsiService[]) => {
    setIsSubmitting(true);
    try {
      onAddInterest(opportunity.id, selectedServices);
      setConfirmModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <StyledCard onClick={handleCardClick} tabIndex={0}>
        <StyledCardMedia>
          <InvestmentTypeBadge type={opportunity.type} />
          {opportunity.mainImageUrl ? (
            <img
              src={opportunity.mainImageUrl}
              alt={opportunity.title || 'Imagen de la oportunidad'}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                userSelect: 'none',
              }}
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
            />
          ) : (
            <PlaceholderIcon />
          )}
          {opportunity.matching && <MatchingIndicator score={opportunity.matching.score} />}
        </StyledCardMedia>

        <OpportunityCardContent opportunity={opportunity}>
          {/* CTA Button below content */}
          {!opportunity.interestStatus || opportunity.interestStatus === 'REMOVED' ? (
            <Box sx={{ mt: 1.5 }}>
              <CTAButton onClick={handleInterestClick}>Me interesa</CTAButton>
            </Box>
          ) : (
            <Box sx={{ mt: 1.5 }}>
              <InterestedButton disabled>Interés mostrado</InterestedButton>
            </Box>
          )}
        </OpportunityCardContent>
      </StyledCard>

      <InterestConfirmationModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmInterest}
        opportunity={opportunity}
        loading={isSubmitting}
      />
    </>
  );
};

interface InterestOpportunityCardProps {
  opportunity: InvestorOpportunitySummaryDTO;
  onDeleteInterest: (id: string) => void;
}
export const InterestOpportunityCard = ({
  opportunity,
  onDeleteInterest,
}: InterestOpportunityCardProps) => {
  const navigate = useNavigate();
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const { mutate: reserveOpportunity } = useReserveOpportunity();
  const { mutate: rejectOpportunity } = useRejectOpportunity();

  const handleCardClick = () => {
    navigate(`/oportunidades/${opportunity.id}`);
  };

  const handleContractSign = (e: React.MouseEvent) => {
    e.stopPropagation();
    setContractModalOpen(true);
  };

  const handleReserveOpportunity = () => {
    reserveOpportunity(opportunity.id, {
      onSuccess: () => {
        enqueueSnackbar(
          <StackedSnackbar
            title="Oportunidad reservada"
            message="Encuéntrala en la sección 'Inversiones'."
          />,
          { variant: 'success' }
        );
      },
      onError: () => {
        enqueueSnackbar(<StackedSnackbar title="Hubo un error al reservar la oportunidad" />, {
          variant: 'error',
        });
      },
    });
  };

  const handleRejectOpportunity = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRejectModalOpen(true);
  };

  const confirmRejectOpportunity = () => {
    rejectOpportunity(opportunity.id, {
      onSuccess: () => {
        enqueueSnackbar(
          <StackedSnackbar
            title="Oportunidad rechazada"
            message="Has rechazado esta oportunidad de inversión."
          />,
          { variant: 'info' }
        );
        setRejectModalOpen(false);
      },
      onError: () => {
        enqueueSnackbar(<StackedSnackbar title="Hubo un error al rechazar la oportunidad" />, {
          variant: 'error',
        });
      },
    });
  };

  return (
    <>
      <StyledCard onClick={handleCardClick} tabIndex={0}>
        <StyledCardMedia>
          <InvestmentTypeBadge type={opportunity.type} />
          {opportunity.mainImageUrl ? (
            <img
              src={opportunity.mainImageUrl}
              alt={opportunity.title || 'Imagen de la oportunidad'}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <PlaceholderIcon />
          )}
          {opportunity.matching && <MatchingIndicator score={opportunity.matching.score} />}
        </StyledCardMedia>

        <OpportunityCardContent opportunity={opportunity} showStatus>
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <OpportunityCTA
              interestStatus={opportunity.interestStatus}
              onDeleteInterest={(e) => {
                e.stopPropagation();
                onDeleteInterest(opportunity.id);
              }}
              onReserve={handleContractSign}
              onReject={handleRejectOpportunity}
              onGoToInvestment={handleCardClick}
            />
          </Box>
        </OpportunityCardContent>
      </StyledCard>

      <ReserveOpportunityModal
        open={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        psiId={opportunity.psi.id}
        onConfirm={handleReserveOpportunity}
      />

      <Dialog
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Confirmas que deseas rechazar la oportunidad?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            El PSI sabrá que no estás interesado en esta oportunidad y se eliminará de tu lista de
            <strong> Mis intereses</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectModalOpen(false)} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmRejectOpportunity} color="secondary" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
